<template>
  <div class="marketplace-home-root" :class="{'dark-mode': darkMode}">

    <!-- Back -->
    <button class="mp-back" @click="goBack" title="Back">
      <i class="mdi mdi-arrow-left" style="font-size:20px;"></i>
    </button>

    <!-- Theme Toggle -->
    <button class="mp-theme-toggle" @click="toggleTheme" :title="darkMode?'Switch to Light':'Switch to Dark'">
      <i :class="darkMode?'mdi mdi-white-balance-sunny':'mdi mdi-moon-waning-crescent'" style="font-size:18px;"></i>
    </button>
    <head>
      <meta charset="utf-8" />
      <title>FLOCKR | Livestock Marketplace</title>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <meta content="Browse and buy livestock across the region" name="description" />
      <meta content="Flockr" name="author" />
      <link rel="shortcut icon" href="/assets/images/favicon.svg">

      <!-- DataTables -->
      <link href="/assets/libs/datatables.net-bs4/css/dataTables.bootstrap4.min.css" rel="stylesheet" type="text/css" />
      <link href="/assets/libs/datatables.net-buttons-bs4/css/buttons.bootstrap4.min.css" rel="stylesheet" type="text/css" />

      <!-- Bootstrap Css -->
      <link href="/assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <!-- Bootstrap select styling -->
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-select@1.13.14/dist/css/bootstrap-select.min.css">
      <!-- Icons Css -->
      <link href="/assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <!-- App Css-->
      <link href="/assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <!-- custom Css-->
      <link href="/assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <!-- div, not <body>: a literal <body> inside a component renders a second body element and breaks browser tooling -->
    <div :data-sidebar="darkMode ? 'dark' : 'light'">
    <div id="layout-wrapper">
      <div>

        <!-- ========== HERO SEARCH ========== -->
        <section class="mp-hero">
          <div class="container">
            <div class="row justify-content-center">
              <div class="col-lg-8 text-center">
                <h1 class="mp-hero-title">Find Your Perfect Livestock</h1>
                <p class="mp-hero-subtitle">Browse thousands of verified listings from farmers across Africa</p>

                <!-- Search Bar -->
                <div class="mp-search-box mt-4">
                  <div class="input-group">
                    <span class="input-group-text mp-search-icon"><i class="mdi mdi-magnify font-size-20"></i></span>
                    <input v-model="searchQuery" type="text" class="form-control mp-search-input"
                           placeholder="Search by breed, species, location..."
                           @keyup.enter="goToSearch">
                    <button class="btn btn-warning text-dark fw-bold px-4" @click="goToSearch">
                      <i class="mdi mdi-magnify me-1"></i> Search
                    </button>
                  </div>
                </div>

                <!-- Location filter — county picker biases the whole page (Featured,
                     Just Listed, Trending, Recommended) to a single county. -->
                <div class="mp-loc-filter mt-2">
                  <i class="mdi mdi-map-marker" style="color:#BFA164;"></i>
                  <select v-model="filterCountyId" @change="onCountyChange" class="form-select form-select-sm mp-loc-select">
                    <option value="">All counties</option>
                    <option v-for="c in counties" :key="c.id" :value="c.id">{{ c.name }}</option>
                  </select>
                </div>

                <!-- Kind switch — live animals vs livestock products (Dev-Requests #4).
                     Also toggles the quick-filter row underneath so buyers see the
                     right chips (breeds for animals, categories for products). -->
                <div class="mt-3 d-flex justify-content-center" style="gap:6px;flex-wrap:wrap;">
                  <span class="mp-kind-tab" :class="{active: kindFilter===''}"                   @click="setKind('')">All</span>
                  <span class="mp-kind-tab" :class="{active: kindFilter==='live_animal'}"        @click="setKind('live_animal')">🐄 Live Animals</span>
                  <span class="mp-kind-tab" :class="{active: kindFilter==='livestock_product'}"  @click="setKind('livestock_product')">🥛 Products (Milk · Eggs · Hides…)</span>
                </div>

                <!-- Quick Filters — horizontal scroller on mobile (mirrors Android's
                     HorizontalScrollView chip strip), centered wrap on desktop. -->
                <div class="mt-3 mp-quick-filter-row">
                  <span class="mp-quick-filter mp-quick-filter-nearme"
                        :class="{active: proximityOn}"
                        @click="toggleProximity"
                        :title="proximityOn ? 'Showing listings near you' : 'Show listings near me'">
                    <i class="mdi mdi-map-marker-radius me-1"></i>
                    <span v-if="proximityOn">Near me · {{ radiusKm }} km</span>
                    <span v-else>📍 Near me</span>
                  </span>
                  <!-- Live-animal chips: species / breed shortcuts -->
                  <template v-if="kindFilter !== 'livestock_product'">
                    <span class="mp-quick-filter" @click="quickSearch('dairy cows')">Dairy Cows</span>
                    <span class="mp-quick-filter" @click="quickSearch('goats')">Goats</span>
                    <span class="mp-quick-filter" @click="quickSearch('dorper sheep')">Dorper Sheep</span>
                    <span class="mp-quick-filter" @click="quickSearch('kienyeji chicken')">Kienyeji</span>
                    <span class="mp-quick-filter" @click="quickSearch('camels')">Camels</span>
                    <span class="mp-quick-filter" @click="quickSearch('pigs')">Pigs</span>
                  </template>
                  <!-- Livestock-product chips: one per category from the API -->
                  <template v-else>
                    <span class="mp-quick-filter"
                          v-for="cat in productCategories" :key="'pcat-'+cat.id"
                          :class="{'active-cat': productCategoryId === cat.id}"
                          @click="pickProductCategory(cat.id)">
                      {{ cat.name }}
                    </span>
                  </template>
                </div>

                <!-- Radius selector — only visible when Near me is active. -->
                <div v-if="proximityOn" class="mt-2 d-flex justify-content-center align-items-center" style="gap:6px;">
                  <small style="color:var(--fm-s,#6B6356);font-size:11px;">Within</small>
                  <select v-model.number="radiusKm" @change="reloadForProximity" class="form-select form-select-sm" style="width:auto;font-size:12px;">
                    <option :value="10">10 km</option>
                    <option :value="25">25 km</option>
                    <option :value="50">50 km</option>
                    <option :value="100">100 km</option>
                    <option :value="250">250 km</option>
                    <option :value="500">500 km</option>
                  </select>
                  <small style="color:var(--fm-s,#6B6356);font-size:11px;">of your location</small>
                </div>

                <!-- Geolocation error hint (e.g. insecure origin, permission denied) -->
                <div v-if="geoError" class="mt-2 mx-auto" style="max-width:520px;padding:8px 12px;background:rgba(255,171,0,.10);border:1px solid rgba(255,171,0,.28);border-radius:8px;color:#B78500;font-size:12px;">
                  <i class="mdi mdi-map-marker-alert me-1"></i>{{ geoError }}
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ========== RECOMMENDED FOR YOU (buyer-seller matching) ==========
             Only shown for signed-in users AND when the API returns at least one
             scored match. Silent otherwise — new signups see no empty "Recommended"
             section. Dev-Requests #1. -->
        <section v-if="recommendedListings.length > 0" class="py-4" style="background:var(--fm-section-a,#F7F5F2);">
          <div class="container">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 class="fw-bold mb-0" style="color:#BFA164;"><i class="mdi mdi-target text-success me-1"></i> Recommended for you</h5>
                <small class="text-muted">
                  <template v-if="recommendedCriteriaTitle">Matched to your request: <b>{{ recommendedCriteriaTitle }}</b></template>
                  <template v-else>Based on your location and browsing</template>
                </small>
              </div>
              <a href="javascript:void(0)" @click="$router.push('/buy-requests/mine')" style="color:#BFA164;font-size:14px;text-decoration:none;font-weight:600;">My Requests <i class="mdi mdi-arrow-right"></i></a>
            </div>

            <div v-if="loadingRecommended" class="text-center py-4">
              <div class="spinner-border text-warning" role="status" style="width:1.5rem;height:1.5rem;"></div>
            </div>

            <div v-else class="row g-3">
              <div :class="['col-lg-3 col-md-4 col-sm-6', viewMode==='grid' ? 'col-6' : 'col-12']" v-for="(item, index) in recommendedListings" :key="'rec-'+index">
                <div class="mp-listing-card" @click="$router.push('/marketplace/listing/' + item.id)">
                  <div class="mp-listing-img">
                    <img v-if="item.primary_photo" :src="photoUrl(item.primary_photo)" :alt="item.title" class="mp-listing-photo" />
                    <div v-else class="mp-listing-placeholder">
                      <i :class="getSpeciesIcon(item.species_name)" style="font-size:48px;color:rgba(191,161,100,0.3);"></i>
                    </div>
                    <div class="mp-listing-badges">
                      <span class="badge" style="background:rgba(80,200,120,.18);color:#50C878;font-weight:600;font-size:10px;"><i class="mdi mdi-target"></i> Match</span>
                    </div>
                  </div>
                  <div class="mp-listing-body">
                    <h6 class="mp-listing-title">{{ item.title }}</h6>
                    <div v-if="item.match_reason" class="mp-listing-meta" style="color:#50C878;font-weight:600;">
                      <i class="mdi mdi-check-circle"></i> {{ item.match_reason }}
                    </div>
                    <div class="mp-listing-location"><i class="mdi mdi-map-marker"></i> {{ item.county_name || 'Location TBD' }}<span v-if="item.distance_km !== undefined && item.distance_km !== null" style="color:#50C878;font-weight:700;margin-left:6px;">· {{ Number(item.distance_km).toFixed(0) }} km</span></div>
                    <div class="mp-listing-footer">
                      <div class="mp-listing-price">{{ (item.currency || 'KES') }} {{ formatPrice(item.price) }} <span class="mp-price-unit">{{ perKgSub(item) || priceUnitLabel(item.price_unit) }}</span></div>
                      <div class="mp-listing-seller">
                        <div class="mp-seller-avatar">{{ getInitials(item.seller_name) }}</div>
                        <span>{{ item.seller_name }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ========== FEATURED LISTINGS ========== -->
        <section class="py-4" style="background:var(--fm-section-b,#FFFFFF);">
          <div class="container">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 class="fw-bold mb-0" style="color:#BFA164;"><i class="mdi mdi-star text-warning me-1"></i> Featured Listings</h5>
                <small class="text-muted">Premium listings from verified sellers</small>
              </div>
              <div class="d-flex align-items-center" style="gap:8px;">
                <!-- Mobile-only list/grid view toggle. Pref persisted in localStorage. -->
                <div class="mp-view-toggle d-md-none" role="group" aria-label="View mode">
                  <button type="button" class="mp-view-btn" :class="{active: viewMode==='list'}" @click="setViewMode('list')" aria-label="List view"><i class="mdi mdi-view-agenda-outline"></i></button>
                  <button type="button" class="mp-view-btn" :class="{active: viewMode==='grid'}" @click="setViewMode('grid')" aria-label="Grid view"><i class="mdi mdi-view-grid-outline"></i></button>
                </div>
                <a href="javascript:void(0)" @click="$router.push({path:'/marketplace/search', query:{featured:'1'}})" style="color:#BFA164;font-size:14px;text-decoration:none;font-weight:600;">See All <i class="mdi mdi-arrow-right"></i></a>
              </div>
            </div>

            <!-- Loading -->
            <div v-if="loadingFeatured" class="text-center py-5">
              <div class="spinner-border text-warning" role="status"><span class="sr-only">Loading...</span></div>
              <p class="mt-2 text-muted">Loading featured listings...</p>
            </div>

            <div v-else class="row g-3">
              <div :class="['col-lg-3 col-md-4 col-sm-6', viewMode==='grid' ? 'col-6' : 'col-12']" v-for="(item, index) in featuredListings" :key="'f-'+index">
                <div class="mp-listing-card" @click="$router.push('/marketplace/listing/' + item.id)">
                  <div class="mp-listing-img">
                    <img v-if="item.primary_photo" :src="photoUrl(item.primary_photo)" :alt="item.title" class="mp-listing-photo" />
                    <div v-else class="mp-listing-placeholder">
                      <i :class="getSpeciesIcon(item.species_name)" style="font-size:48px;color:rgba(191,161,100,0.3);"></i>
                    </div>
                    <div class="mp-listing-badges">
                      <span class="badge bg-warning bg-opacity-25 text-warning" style="font-size:10px;"><i class="mdi mdi-star"></i> Featured</span>
                      <span v-if="item.seller_verified" class="badge bg-success bg-opacity-25 text-success" style="font-size:10px;" title="Seller has completed KYC identity verification"><i class="mdi mdi-check-decagram"></i> Seller Verified</span><span v-if="Number(item.vet_verified) === 1" class="badge bg-info bg-opacity-25 text-info" style="font-size:10px;" title="Animal inspected and verified by a vet on Flockr"><i class="mdi mdi-stethoscope"></i> Vet Verified</span><span v-if="item.anitrac_id && item.anitrac_verified_at" class="badge" style="background:rgba(80,200,120,.18);color:#50C878;font-weight:600;" :title="'ANITRAC: ' + item.anitrac_id + ' (verified)'"><i class="mdi mdi-shield-check me-1"></i>ANITRAC ✓</span><span v-else-if="item.anitrac_id" class="badge" style="background:rgba(160,152,136,.18);color:#A09888;font-weight:600;" :title="'ANITRAC: ' + item.anitrac_id + ' (pending verification)'"><i class="mdi mdi-shield-outline me-1"></i>ANITRAC pending</span>
                    </div>
                    <button class="mp-save-btn" @click.stop="toggleSave(item)">
                      <i :class="item.is_saved ? 'mdi mdi-heart text-danger' : 'mdi mdi-heart-outline'"></i>
                    </button>
                  </div>
                  <div class="mp-listing-body">
                    <h6 class="mp-listing-title">{{ item.title }}</h6>
                    <div class="mp-listing-meta">
                      <span v-if="item.weight"><i class="mdi mdi-weight-kilogram"></i> {{ item.weight }} kg</span>
                      <span v-if="item.sex"><i class="mdi mdi-gender-male-female"></i> {{ item.sex }}</span>
                      <span v-if="item.age_text"><i class="mdi mdi-clock-outline"></i> {{ item.age_text }}</span>
                    </div>
                    <div class="mp-listing-location"><i class="mdi mdi-map-marker"></i> {{ item.county_name || item.location || 'Location TBD' }}<span v-if="item.distance_km !== undefined && item.distance_km !== null" style="color:#BFA164;font-weight:700;margin-left:6px;">· {{ Number(item.distance_km).toFixed(0) }} km away</span></div>
                    <div class="mp-listing-footer">
                      <div class="mp-listing-price">{{ (item.currency || 'KES') }} {{ formatPrice(item.price) }} <span class="mp-price-unit">{{ perKgSub(item) || priceUnitLabel(item.price_unit) }}</span></div>
                      <div class="mp-listing-seller">
                        <div class="mp-seller-avatar">{{ getInitials(item.seller_name) }}</div>
                        <span>{{ item.seller_name }}</span>
                        <i class="mdi mdi-star text-warning" v-if="item.seller_rating"></i>
                        <span v-if="item.seller_rating">{{ item.seller_rating }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="featuredListings.length === 0 && !loadingFeatured" class="col-12">
                <div class="text-center py-5">
                  <i class="mdi mdi-star-outline" style="font-size:48px;color:#BFA164;"></i>
                  <p class="text-muted mt-2">No featured listings at the moment</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ========== RECENT / LATEST LISTINGS ========== -->
        <section class="py-4" style="background:var(--fm-section-a,#F7F5F2);">
          <div class="container">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 class="fw-bold mb-0" style="color:#BFA164;"><i class="mdi mdi-clock-fast text-info me-1"></i> Just Listed</h5>
                <small class="text-muted">Newest arrivals on the marketplace</small>
              </div>
              <a href="javascript:void(0)" @click="$router.push({path:'/marketplace/search', query:{sort:'newest'}})" style="color:#BFA164;font-size:14px;text-decoration:none;font-weight:600;">See All <i class="mdi mdi-arrow-right"></i></a>
            </div>

            <div v-if="loadingRecent" class="text-center py-5">
              <div class="spinner-border text-warning" role="status"><span class="sr-only">Loading...</span></div>
            </div>

            <div v-else class="row g-3">
              <div :class="['col-lg-3 col-md-4 col-sm-6', viewMode==='grid' ? 'col-6' : 'col-12']" v-for="(item, index) in recentListings" :key="'r-'+index">
                <div class="mp-listing-card" @click="$router.push('/marketplace/listing/' + item.id)">
                  <div class="mp-listing-img">
                    <img v-if="item.primary_photo" :src="photoUrl(item.primary_photo)" :alt="item.title" class="mp-listing-photo" />
                    <div v-else class="mp-listing-placeholder">
                      <i :class="getSpeciesIcon(item.species_name)" style="font-size:48px;color:rgba(191,161,100,0.3);"></i>
                    </div>
                    <div class="mp-listing-badges">
                      <span v-if="item.seller_verified" class="badge bg-success bg-opacity-25 text-success" style="font-size:10px;" title="Seller has completed KYC identity verification"><i class="mdi mdi-check-decagram"></i> Seller Verified</span><span v-if="Number(item.vet_verified) === 1" class="badge bg-info bg-opacity-25 text-info" style="font-size:10px;" title="Animal inspected and verified by a vet on Flockr"><i class="mdi mdi-stethoscope"></i> Vet Verified</span><span v-if="item.anitrac_id && item.anitrac_verified_at" class="badge" style="background:rgba(80,200,120,.18);color:#50C878;font-weight:600;" :title="'ANITRAC: ' + item.anitrac_id + ' (verified)'"><i class="mdi mdi-shield-check me-1"></i>ANITRAC ✓</span><span v-else-if="item.anitrac_id" class="badge" style="background:rgba(160,152,136,.18);color:#A09888;font-weight:600;" :title="'ANITRAC: ' + item.anitrac_id + ' (pending verification)'"><i class="mdi mdi-shield-outline me-1"></i>ANITRAC pending</span>
                    </div>
                    <button class="mp-save-btn" @click.stop="toggleSave(item)">
                      <i :class="item.is_saved ? 'mdi mdi-heart text-danger' : 'mdi mdi-heart-outline'"></i>
                    </button>
                    <div class="mp-listing-time-badge">
                      <i class="mdi mdi-clock-outline"></i> {{ timeAgo(item.created_at) }}
                    </div>
                  </div>
                  <div class="mp-listing-body">
                    <h6 class="mp-listing-title">{{ item.title }}</h6>
                    <div class="mp-listing-meta">
                      <span v-if="item.weight"><i class="mdi mdi-weight-kilogram"></i> {{ item.weight }} kg</span>
                      <span v-if="item.sex"><i class="mdi mdi-gender-male-female"></i> {{ item.sex }}</span>
                      <span v-if="item.age_text"><i class="mdi mdi-clock-outline"></i> {{ item.age_text }}</span>
                    </div>
                    <div class="mp-listing-location"><i class="mdi mdi-map-marker"></i> {{ item.county_name || item.location || 'Location TBD' }}<span v-if="item.distance_km !== undefined && item.distance_km !== null" style="color:#BFA164;font-weight:700;margin-left:6px;">· {{ Number(item.distance_km).toFixed(0) }} km away</span></div>
                    <div class="mp-listing-footer">
                      <div class="mp-listing-price">{{ (item.currency || 'KES') }} {{ formatPrice(item.price) }} <span class="mp-price-unit">{{ perKgSub(item) || priceUnitLabel(item.price_unit) }}</span></div>
                      <div class="mp-listing-seller">
                        <div class="mp-seller-avatar">{{ getInitials(item.seller_name) }}</div>
                        <span>{{ item.seller_name }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="recentListings.length === 0 && !loadingRecent" class="col-12">
                <div class="text-center py-5">
                  <i class="mdi mdi-package-variant" style="font-size:48px;color:#BFA164;"></i>
                  <p class="text-muted mt-2">No listings yet. Be the first to sell!</p>
                  <a href="javascript:void(0)" @click="$router.push('/sell/create')" class="btn btn-warning text-dark fw-bold"><i class="mdi mdi-plus me-1"></i> Create Listing</a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ========== POPULAR / TRENDING ========== -->
        <section class="py-4" style="background:var(--fm-section-b,#FFFFFF);">
          <div class="container">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 class="fw-bold mb-0" style="color:#BFA164;"><i class="mdi mdi-trending-up text-success me-1"></i> Trending Now</h5>
                <small class="text-muted">Most viewed listings this week</small>
              </div>
              <a href="javascript:void(0)" @click="$router.push({path:'/marketplace/search', query:{sort:'popular'}})" style="color:#BFA164;font-size:14px;text-decoration:none;font-weight:600;">See All <i class="mdi mdi-arrow-right"></i></a>
            </div>

            <div v-if="loadingTrending" class="text-center py-5">
              <div class="spinner-border text-warning" role="status"><span class="sr-only">Loading...</span></div>
            </div>

            <div v-else class="row g-3">
              <div :class="['col-lg-3 col-md-4 col-sm-6', viewMode==='grid' ? 'col-6' : 'col-12']" v-for="(item, index) in trendingListings" :key="'t-'+index">
                <div class="mp-listing-card" @click="$router.push('/marketplace/listing/' + item.id)">
                  <div class="mp-listing-img">
                    <img v-if="item.primary_photo" :src="photoUrl(item.primary_photo)" :alt="item.title" class="mp-listing-photo" />
                    <div v-else class="mp-listing-placeholder">
                      <i :class="getSpeciesIcon(item.species_name)" style="font-size:48px;color:rgba(191,161,100,0.3);"></i>
                    </div>
                    <div class="mp-listing-badges">
                      <span v-if="item.seller_verified" class="badge bg-success bg-opacity-25 text-success" style="font-size:10px;" title="Seller has completed KYC identity verification"><i class="mdi mdi-check-decagram"></i> Seller Verified</span><span v-if="Number(item.vet_verified) === 1" class="badge bg-info bg-opacity-25 text-info" style="font-size:10px;" title="Animal inspected and verified by a vet on Flockr"><i class="mdi mdi-stethoscope"></i> Vet Verified</span><span v-if="item.anitrac_id && item.anitrac_verified_at" class="badge" style="background:rgba(80,200,120,.18);color:#50C878;font-weight:600;" :title="'ANITRAC: ' + item.anitrac_id + ' (verified)'"><i class="mdi mdi-shield-check me-1"></i>ANITRAC ✓</span><span v-else-if="item.anitrac_id" class="badge" style="background:rgba(160,152,136,.18);color:#A09888;font-weight:600;" :title="'ANITRAC: ' + item.anitrac_id + ' (pending verification)'"><i class="mdi mdi-shield-outline me-1"></i>ANITRAC pending</span>
                    </div>
                    <button class="mp-save-btn" @click.stop="toggleSave(item)">
                      <i :class="item.is_saved ? 'mdi mdi-heart text-danger' : 'mdi mdi-heart-outline'"></i>
                    </button>
                    <div class="mp-listing-views-badge">
                      <i class="mdi mdi-eye"></i> {{ item.view_count || 0 }}
                    </div>
                  </div>
                  <div class="mp-listing-body">
                    <h6 class="mp-listing-title">{{ item.title }}</h6>
                    <div class="mp-listing-meta">
                      <span v-if="item.weight"><i class="mdi mdi-weight-kilogram"></i> {{ item.weight }} kg</span>
                      <span v-if="item.sex"><i class="mdi mdi-gender-male-female"></i> {{ item.sex }}</span>
                      <span v-if="item.age_text"><i class="mdi mdi-clock-outline"></i> {{ item.age_text }}</span>
                    </div>
                    <div class="mp-listing-location"><i class="mdi mdi-map-marker"></i> {{ item.county_name || item.location || 'Location TBD' }}<span v-if="item.distance_km !== undefined && item.distance_km !== null" style="color:#BFA164;font-weight:700;margin-left:6px;">· {{ Number(item.distance_km).toFixed(0) }} km away</span></div>
                    <div class="mp-listing-footer">
                      <div class="mp-listing-price">{{ (item.currency || 'KES') }} {{ formatPrice(item.price) }} <span class="mp-price-unit">{{ perKgSub(item) || priceUnitLabel(item.price_unit) }}</span></div>
                      <div class="mp-listing-seller">
                        <div class="mp-seller-avatar">{{ getInitials(item.seller_name) }}</div>
                        <span>{{ item.seller_name }}</span>
                        <i class="mdi mdi-star text-warning" v-if="item.seller_rating"></i>
                        <span v-if="item.seller_rating">{{ item.seller_rating }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ========== BUY REQUESTS ========== -->
        <section class="py-4" style="background:var(--fm-section-a,#F7F5F2);">
          <div class="container">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 class="fw-bold mb-0" style="color:#BFA164;"><i class="mdi mdi-bullhorn text-warning me-1"></i> Active Buy Requests</h5>
                <small class="text-muted">Buyers looking for livestock — can you supply?</small>
              </div>
              <div>
                <a href="javascript:void(0)" @click="$router.push('/marketplace/buy-requests/create')" class="btn btn-sm btn-outline-warning me-2"><i class="mdi mdi-plus"></i> Post Request</a>
                <a href="javascript:void(0)" @click="$router.push('/marketplace/buy-requests')" style="color:#BFA164;font-size:14px;text-decoration:none;font-weight:600;">View All <i class="mdi mdi-arrow-right"></i></a>
              </div>
            </div>

            <div v-if="loadingBuyRequests" class="text-center py-4">
              <div class="spinner-border text-warning" role="status"><span class="sr-only">Loading...</span></div>
            </div>

            <div v-else class="row g-3">
              <div class="col-lg-4 col-md-6" v-for="(req, ri) in buyRequests" :key="'br-'+ri">
                <div class="mp-buyrequest-card" @click="$router.push('/marketplace/buy-requests/' + req.id)">
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <span class="badge bg-warning bg-opacity-10 text-warning">{{ req.species_name || 'Livestock' }}</span>
                    <small class="text-muted">{{ timeAgo(req.created_at) }}</small>
                  </div>
                  <h6 class="fw-bold mb-1" style="color:var(--fm-h,#1A1A1A);font-size:15px;">{{ req.title }}</h6>
                  <p class="mb-2" style="font-size:13px;color:var(--fm-m,#555);line-height:1.5;">{{ truncate(req.description, 80) }}</p>
                  <div class="d-flex flex-wrap mb-2" style="gap:12px;font-size:12px;color:var(--fm-s,#6B6356);">
                    <span><i class="mdi mdi-numeric"></i> Qty: {{ req.quantity_needed }}</span>
                    <span><i class="mdi mdi-cash"></i> Budget: {{ (req.currency || 'KES') }} {{ formatPrice(req.budget_min) }} - {{ formatPrice(req.budget_max) }}</span>
                    <span><i class="mdi mdi-map-marker"></i> {{ req.county_name || 'Any location' }}</span>
                  </div>
                  <div class="d-flex justify-content-between align-items-center pt-2" style="border-top:1px solid var(--fm-br,#2A2520);">
                    <div class="d-flex align-items-center" style="gap:6px;">
                      <div class="mp-seller-avatar">{{ getInitials(req.buyer_name) }}</div>
                      <span style="font-size:13px;color:var(--fm-s,#777);">{{ req.buyer_name }}</span>
                    </div>
                    <span class="badge bg-info bg-opacity-10 text-info" style="font-size:11px;">{{ req.response_count || 0 }} responses</span>
                  </div>
                </div>
              </div>
              <div v-if="buyRequests.length === 0 && !loadingBuyRequests" class="col-12">
                <div class="text-center py-4">
                  <i class="mdi mdi-bullhorn-outline" style="font-size:48px;color:#BFA164;"></i>
                  <p class="text-muted mt-2">No active buy requests</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ========== SELL CTA ========== -->
        <section class="py-5" style="background:linear-gradient(135deg,var(--fm-hero1,#1A1510),var(--fm-hero2,#2A2018));border-top:1px solid var(--fm-br,#E0D8D0);">
          <div class="container">
            <div class="row align-items-center">
              <div class="col-lg-8">
                <h3 class="fw-bold" style="color:#BFA164;">Have Livestock to Sell?</h3>
                <p class="mb-0" style="color:var(--fm-m,#A09888);font-size:16px;">List your animals in minutes. Reach thousands of verified buyers. Get paid securely with escrow protection.</p>
              </div>
              <div class="col-lg-4 text-lg-end mt-3 mt-lg-0">
                <a href="javascript:void(0)" @click="$router.push('/sell/create')" class="btn btn-warning text-dark fw-bold btn-lg"><i class="mdi mdi-plus me-1"></i> Create Listing</a>
                <a href="javascript:void(0)" @click="$router.push('/marketplace/buy-requests')" class="btn btn-outline-warning btn-lg ms-2"><i class="mdi mdi-bullhorn me-1"></i> Buy Requests</a>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
    <div class="rightbar-overlay"></div>
    </div>
  </div>
</template>

<script>
import { executeJson, flocker, uploads } from "@/api";

export default {
  name: "MarketplaceHome",

  data() {
    return {
      // Theme
      darkMode: localStorage.getItem('flockr_theme') === 'dark',
      themeObserver: null,

      // Search
      searchQuery: '',
      searchCounty: '',
      counties: [],
      filterCountyId: '', // county-based location filter on home page

      // Listings
      featuredListings: [],
      recentListings: [],
      trendingListings: [],
      buyRequests: [],
      recommendedListings: [],
      recommendedCriteriaTitle: '',

      // Near-me proximity filter (Dev-Requests #5 — extended to home page)
      proximityOn: false,
      buyerLocation: { lat: null, lng: null },
      radiusKm: 100,
      geoBusy: false,
      geoError: '',

      // Kind filter (Dev-Requests #4): '' = all, 'live_animal', 'livestock_product'
      kindFilter: '',
      productCategoryId: '',
      productCategories: [],

      // Loading states
      loadingFeatured: false,
      loadingRecent: false,
      loadingTrending: false,
      loadingBuyRequests: false,
      loadingRecommended: false,


      // Mobile view mode — 'list' = 1-column big cards, 'grid' = 2-column compact.
      // Persisted in localStorage so the user's choice carries across sessions.
      viewMode: localStorage.getItem('flockr_mkt_view') === 'grid' ? 'grid' : 'list'
    };
  },

  mounted() {
    this.syncTheme();
    this.themeObserver = new MutationObserver(() => this.syncTheme());
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
    this.initializePage();
  },

  beforeUnmount() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  },

  methods: {
    setViewMode(mode){
      // Toggle between 'list' (1-col big cards) and 'grid' (2-col compact) on mobile.
      // Persist so the user's choice survives navigation/refresh.
      this.viewMode = mode === 'grid' ? 'grid' : 'list';
      localStorage.setItem('flockr_mkt_view', this.viewMode);
    },
    syncTheme(){
      const t = document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || (this.darkMode ? 'dark' : 'light');
      this.darkMode = t === 'dark';
      window.__flockrSetTheme(this.darkMode ? 'dark' : 'light');

      if (document.body) {
        document.body.setAttribute('data-sidebar', this.darkMode ? 'dark' : 'light');
        document.body.style.background = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        document.body.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }

      const wrapper = document.getElementById('layout-wrapper');
      if (wrapper) {
        wrapper.style.background = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        wrapper.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }
    },

    toggleTheme(){
      this.darkMode = !this.darkMode;
      localStorage.setItem('flockr_theme', this.darkMode ? 'dark' : 'light');
      this.syncTheme();
    },
    goBack(){ if(window.history.length>1) this.$router.go(-1); else this.$router.push('/dashboard'); },
    photoUrl(p){if(!p)return'';if(p.startsWith&&p.startsWith('http'))return p;return uploads+String(p).replace('uploads/','')},

    async initializePage() {
      this.fetchCounties();
      this.fetchRecommendedListings();
      this.fetchFeaturedListings();
      this.fetchRecentListings();
      this.fetchTrendingListings();
      this.fetchBuyRequests();
    },

    // Dev-Requests #1 — pull the API's top scored matches for the current buyer.
    // Silent when signed out or when API returns no matches.
    async fetchRecommendedListings() {
      const userId = sessionStorage.getItem('userId');
      if (!userId) return;
      this.loadingRecommended = true;
      try {
        const response = await executeJson({ function: "getBuyerRecommendations", buyerId: userId, limit: 5 }, flocker);
        if (response.data.success) {
          this.recommendedListings = response.data.data.listings || [];
          this.recommendedCriteriaTitle = response.data.data.criteria_used?.title || '';
        }
      } catch (e) {
        console.error('Error fetching recommendations:', e);
      } finally {
        this.loadingRecommended = false;
      }
    },

    // ============ API CALLS ============

    async fetchCounties() {
      try {
        const response = await executeJson({ function: "getCounties", countryCode: "KE" }, flocker);
        if (response.data.success) {
          this.counties = response.data.data.counties || [];
        }
      } catch (e) {
        console.error('Error fetching counties:', e);
      }
    },

    // Merge proximity + county + kind filters into every listing fetch. Kept in one
    // place so we can extend later without duplicating.
    buildBaseParams(extra) {
      const p = Object.assign({ function: "searchListings", limit: 8, page: 1 }, extra || {});
      if (this.proximityOn && this.buyerLocation.lat && this.buyerLocation.lng) {
        p.latitude  = this.buyerLocation.lat;
        p.longitude = this.buyerLocation.lng;
        p.radiusKm  = this.radiusKm;
        if (!p.sortBy || p.sortBy === 'newest') p.sortBy = 'nearest';
      }
      if (this.filterCountyId) p.countyIds = String(this.filterCountyId);
      if (this.kindFilter)         p.listingKind        = this.kindFilter;
      if (this.productCategoryId)  p.productCategoryIds = String(this.productCategoryId);
      return p;
    },
    async ensureProductCategories() {
      if (this.productCategories.length) return;
      try {
        const r = await executeJson({ function: 'getLivestockProductCategories' }, flocker);
        if (r.data && r.data.success) this.productCategories = r.data.data.categories || [];
      } catch(e) { console.error(e); }
    },
    setKind(k) {
      this.kindFilter = k;
      this.productCategoryId = '';
      if (k === 'livestock_product') this.ensureProductCategories();
      this.reloadAll();
    },
    pickProductCategory(id) {
      this.productCategoryId = this.productCategoryId === id ? '' : id;
      this.reloadAll();
    },
    reloadAll() {
      this.fetchFeaturedListings();
      this.fetchRecentListings();
      this.fetchTrendingListings();
      this.fetchRecommendedListings();
    },

    // Near-me toggle — grabs geolocation on first activate and caches for the session.
    // Geolocation only works on secure origins (HTTPS or localhost). On a LAN IP or
    // plain-http host we fall back to the county picker with a helpful hint.
    toggleProximity() {
      if (this.proximityOn) { this.proximityOn = false; this.reloadForProximity(); return; }
      if (this.buyerLocation.lat && this.buyerLocation.lng) {
        this.proximityOn = true; this.reloadForProximity(); return;
      }
      if (!navigator.geolocation) {
        this.geoError = 'Your browser does not support location. Pick a county from the dropdown instead.';
        return;
      }
      // Detect insecure origin (Chrome + Safari block getCurrentPosition on plain http).
      const isSecure = window.isSecureContext || location.protocol === 'https:' || location.hostname === 'localhost' || location.hostname === '127.0.0.1';
      if (!isSecure) {
        this.geoError = 'Location needs an HTTPS site. Pick a county from the dropdown below instead.';
        return;
      }
      this.geoBusy = true;
      this.geoError = '';
      const self = this;
      navigator.geolocation.getCurrentPosition(function(pos){
        self.buyerLocation = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        self.proximityOn = true;
        self.geoBusy = false;
        self.reloadForProximity();
      }, function(err){
        self.geoBusy = false;
        const reason = err && err.code === 1 ? 'you blocked location access' :
                       err && err.code === 2 ? 'position unavailable' :
                       err && err.code === 3 ? 'took too long — check your network' :
                       (err && err.message) || 'unknown error';
        self.geoError = 'Could not get your location (' + reason + '). Pick a county from the dropdown below instead.';
      }, { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 });
    },
    reloadForProximity() { this.reloadAll(); },
    onCountyChange()      { this.reloadAll(); },

    async fetchFeaturedListings() {
      this.loadingFeatured = true;
      try {
        const response = await executeJson(this.buildBaseParams({ limit: 8, sortBy: "newest" }), flocker);
        if (response.data.success) {
          this.featuredListings = response.data.data.listings || [];
        }
      } catch (e) {
        console.error('Error fetching featured listings:', e);
      } finally {
        this.loadingFeatured = false;
      }
    },

    async fetchRecentListings() {
      this.loadingRecent = true;
      try {
        const response = await executeJson(this.buildBaseParams({ limit: 8, sortBy: "newest" }), flocker);
        if (response.data.success) {
          this.recentListings = response.data.data.listings || [];
        }
      } catch (e) {
        console.error('Error fetching recent listings:', e);
      } finally {
        this.loadingRecent = false;
      }
    },

    async fetchTrendingListings() {
      this.loadingTrending = true;
      try {
        const response = await executeJson(this.buildBaseParams({ limit: 8, sortBy: "popular" }), flocker);
        if (response.data.success) {
          this.trendingListings = response.data.data.listings || [];
        }
      } catch (e) {
        console.error('Error fetching trending listings:', e);
      } finally {
        this.loadingTrending = false;
      }
    },

    async fetchBuyRequests() {
      this.loadingBuyRequests = true;
      try {
        const response = await executeJson({
          function: "getBuyRequests",
          status: "active",
          limit: 3,
          page: 1
        }, flocker);
        if (response.data.success) {
          this.buyRequests = response.data.data.buyRequests || [];
        }
      } catch (e) {
        console.error('Error fetching buy requests:', e);
      } finally {
        this.loadingBuyRequests = false;
      }
    },

    async toggleSave(item) {
      const userId = sessionStorage.getItem('userId');
      if (!userId) {
        this.$router.push('/login');
        return;
      }
      try {
        const response = await executeJson({
          function: "saveListing",
          userId: userId,
          listingId: item.id
        }, flocker);
        if (response.data.success) {
          item.is_saved = !item.is_saved;
        }
      } catch (e) {
        console.error('Error toggling save:', e);
      }
    },

    // ============ NAVIGATION ============

    goToSearch() {
      const query = {};
      if (this.searchQuery) query.keyword = this.searchQuery;
      if (this.searchCounty) query.county = this.searchCounty;
      this.$router.push({ path: '/marketplace/search', query });
    },

    quickSearch(term) {
      this.searchQuery = term;
      this.goToSearch();
    },

    // ============ HELPERS ============

    formatPrice(val) {
      if (!val) return '0';
      return Number(val).toLocaleString('en-KE');
    },

    // Pilot-reviewer fix (issue #2): "KES 280" alone was ambiguous, so we
    // render the unit alongside the price on every card.
    priceUnitLabel(u) {
      switch (u) {
        case 'per_kg_live':    return '/ kg live';
        case 'per_kg_dressed': return '/ kg dressed';
        case 'per_batch':      return '/ batch';
        case 'per_liter':
        case 'per_litre':      return '/ litre';
        case 'per_head':       return '/ head';
        // product units (per_piece, per_tray, per_kg, per_dozen, ...)
        default:               return u ? '/ ' + String(u).replace(/^per_/, '').replace(/_/g, ' ') : '';
      }
    },

    // Issue #82 — per-kg listings store the computed TOTAL in price, so show
    // the math (rate/kg × weight) instead of a misleading "/kg" suffix.
    perKgSub(item) {
      if (String((item && item.price_unit) || '').indexOf('per_kg') !== 0) return '';
      var w = Number(item.weight_kg) || 0, p = Number(item.price) || 0;
      if (w <= 0 || p <= 0) return '';
      var rate = Math.round((p / w) * 100) / 100;
      return '(' + this.formatPrice(rate) + '/kg × ' + w + ' kg)';
    },

    getInitials(name) {
      if (!name) return '?';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return name.slice(0, 2).toUpperCase();
    },

    getSpeciesIcon(speciesName) {
      if (!speciesName) return 'mdi mdi-paw';
      const s = speciesName.toLowerCase();
      if (s.includes('cattle') || s.includes('cow') || s.includes('bull') || s.includes('heifer')) return 'mdi mdi-cow';
      if (s.includes('goat')) return 'mdi mdi-alpha-g-circle';
      if (s.includes('sheep') || s.includes('ram') || s.includes('ewe') || s.includes('dorper')) return 'mdi mdi-sheep';
      if (s.includes('poultry') || s.includes('chicken') || s.includes('kienyeji') || s.includes('bird')) return 'mdi mdi-bird';
      if (s.includes('pig') || s.includes('sow') || s.includes('boar')) return 'mdi mdi-pig';
      if (s.includes('camel')) return 'mdi mdi-alpha-c-circle';
      return 'mdi mdi-paw';
    },

    getSpeciesBgColor(speciesName) {
      if (!speciesName) return 'rgba(191,161,100,.12)';
      const s = speciesName.toLowerCase();
      if (s.includes('cattle') || s.includes('cow') || s.includes('bull') || s.includes('heifer')) return 'rgba(191,161,100,.12)';
      if (s.includes('goat')) return 'rgba(52,195,143,.12)';
      if (s.includes('sheep') || s.includes('ram') || s.includes('ewe') || s.includes('dorper')) return 'rgba(80,165,241,.12)';
      if (s.includes('poultry') || s.includes('chicken') || s.includes('kienyeji') || s.includes('bird')) return 'rgba(241,180,76,.12)';
      if (s.includes('pig') || s.includes('sow') || s.includes('boar')) return 'rgba(232,67,147,.12)';
      if (s.includes('camel')) return 'rgba(155,89,182,.12)';
      return 'rgba(191,161,100,.12)';
    },

    getSpeciesIconColor(speciesName) {
      if (!speciesName) return '#BFA164';
      const s = speciesName.toLowerCase();
      if (s.includes('cattle') || s.includes('cow') || s.includes('bull') || s.includes('heifer')) return '#BFA164';
      if (s.includes('goat')) return '#34c38f';
      if (s.includes('sheep') || s.includes('ram') || s.includes('ewe') || s.includes('dorper')) return '#50a5f1';
      if (s.includes('poultry') || s.includes('chicken') || s.includes('kienyeji') || s.includes('bird')) return '#f1b44c';
      if (s.includes('pig') || s.includes('sow') || s.includes('boar')) return '#e84393';
      if (s.includes('camel')) return '#9b59b6';
      return '#BFA164';
    },

    timeAgo(dateStr) {
      if (!dateStr) return '';
      const now = new Date();
      const date = new Date(dateStr);
      const diff = Math.floor((now - date) / 1000);
      if (diff < 60) return 'Just now';
      if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
      if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
      if (diff < 604800) return Math.floor(diff / 86400) + 'd ago';
      return Math.floor(diff / 604800) + 'w ago';
    },

    truncate(text, length) {
      if (!text) return '';
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    }
  }
};
</script>


<style>
:root{
  --fm-h:#F5F0E8;
  --fm-s:#A09888;
  --fm-m:#A09888;
  --fm-bg1:#141414;
  --fm-bg2:#0F0F0F;
  --fm-bg3:#1E1E1E;
  --fm-br:#2A2520;
  --fm-page:#0A0A0A;
  --fm-hero1:#1A1510;
  --fm-hero2:#2A2018;
  --fm-section-a:#0F0F0F;
  --fm-section-b:#141414;
}

html[data-theme="light"]{
  --fm-h:#1A1A1A;
  --fm-s:#777777;
  --fm-m:#555555;
  --fm-bg1:#FFFFFF;
  --fm-bg2:#F7F5F2;
  --fm-bg3:#F0EBE4;
  --fm-br:#E8E2DA;
  --fm-page:#F7F5F2;
  --fm-hero1:#FFFDF9;
  --fm-hero2:#F7F5F2;
  --fm-section-a:#F7F5F2;
  --fm-section-b:#FFFFFF;
}

html[data-theme="dark"]{
  --fm-page:#0A0A0A;
  --fm-hero1:#1A1510;
  --fm-hero2:#2A2018;
  --fm-section-a:#0F0F0F;
  --fm-section-b:#141414;
}

html[data-theme="light"] body,
html[data-theme="light"] #layout-wrapper,
html[data-theme="light"] .marketplace-home-root{
  background:var(--fm-page)!important;
  color:var(--fm-h)!important;
}

html[data-theme="dark"] body,
html[data-theme="dark"] #layout-wrapper,
html[data-theme="dark"] .marketplace-home-root{
  background:var(--fm-page)!important;
  color:var(--fm-h)!important;
}
</style>

<style scoped>
/* HERO */
.mp-hero {
  background: linear-gradient(135deg, var(--fm-hero1,#1A1510) 0%, var(--fm-hero2,#2A2018) 50%, var(--fm-hero1,#1A1510) 100%)!important;
  padding: 80px 0 50px;
  position: relative;
  overflow: hidden;
}
.mp-hero::before {
  content: '';
  position: absolute;
  top: -40%;
  right: -15%;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(191,161,100,.06) 0%, transparent 70%);
  border-radius: 50%;
}
.mp-hero-title { font-size: 42px; font-weight: 800; color: var(--fm-h,#F5F0E8)!important; line-height: 1.1; }
.mp-hero-subtitle { font-size: 17px; color: var(--fm-s,#A09888)!important; margin-top: 8px; }

/* SEARCH */
.mp-search-box { max-width: 720px; margin: 0 auto; }
.mp-search-box .input-group { background: #FFFFFF!important; border: 1px solid #E8E2DA; border-radius: 12px; overflow: hidden; }
.mp-search-icon { background: #141414 !important; border: none !important; color: #6B6356 !important; }
.mp-search-input { background: transparent !important; border: none !important; color: var(--fm-h,#F5F0E8) !important; font-size: 15px; }
.mp-search-input::placeholder { color: #6B6356 !important; }
.mp-search-input:focus { box-shadow: none !important; }
.mp-search-select { background: #F7F5F2 !important; border: none !important; border-left: 1px solid #E0D8D0 !important; color: #555 !important; max-width: 180px; font-size: 14px; }
.mp-search-select:focus { box-shadow: none !important; }

.mp-quick-filter {
  display: inline-block;
  background: rgba(191,161,100,.08)!important;
  color: var(--fm-s,#A09888)!important;
  padding: 5px 14px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all .2s;
  white-space: nowrap;
}
.mp-quick-filter:hover { background: rgba(191,161,100,.18)!important; color: #BFA164!important; }
.mp-quick-filter.active-cat { background:#BFA164!important; color:#FFFFFF!important; font-weight:700; }
.mp-kind-tab {
  display: inline-block;
  background: rgba(191,161,100,.05);
  color: var(--fm-s,#A09888);
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all .15s;
  border: 1px solid transparent;
}
.mp-kind-tab:hover { background: rgba(191,161,100,.15); color: #BFA164; }
.mp-kind-tab.active { background:#BFA164; color:#FFFFFF; border-color:#BFA164; }
.mp-quick-filter-nearme.active {
  background: #50C878!important;
  color: #FFFFFF!important;
  font-weight: 700;
}
.mp-quick-filter-nearme.active:hover { background: #45B36B!important; color: #FFFFFF!important; }
.mp-loc-filter {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(191,161,100,.08);
  border-radius: 50px;
  padding: 4px 12px;
}
.mp-loc-select {
  border: none!important;
  background: transparent!important;
  box-shadow: none!important;
  color: #1A1A1A!important;
  font-size: 13px;
  font-weight: 500;
  padding: 4px 8px!important;
  min-width: 140px;
}
.dark-mode .mp-loc-select { color: #F5F0E8!important; }
.mp-loc-select:focus { outline: none; box-shadow: none; }
/* Chip row container — wraps on desktop, horizontal-scrolls on mobile (matches Android). */
.mp-quick-filter-row {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
}
@media (max-width: 768px) {
  .mp-quick-filter-row {
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    justify-content: flex-start;
    padding: 4px 12px 6px;
    margin-left: -12px;
    margin-right: -12px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .mp-quick-filter-row::-webkit-scrollbar { display: none; }
  .mp-quick-filter { flex: 0 0 auto; }
}

/* SPECIES CARDS */
.mp-species-card {
  background: #FFFFFF!important;
  border: 1px solid #E8E2DA;
  border-radius: 14px;
  padding: 20px 12px;
  text-align: center;
  cursor: pointer;
  transition: all .3s;
}
.mp-species-card:hover { border-color: #BFA164; transform: translateY(-3px); }
.mp-species-icon { width: 60px; height: 60px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 10px; }
.mp-species-name { font-size: 14px; font-weight: 700; color: #1A1A1A!important; }
.mp-species-count { font-size: 12px; color: #6B6356!important; margin-top: 2px; }

/* LISTING CARDS */
.mp-listing-card {
  background: #FFFFFF!important;
  border: 1px solid #E8E2DA;
  border-radius: 14px;
  overflow: hidden;
  transition: all .3s;
  cursor: pointer;
  height: 100%;
}
.mp-listing-card:hover { border-color: #BFA164; transform: translateY(-3px); }
.mp-listing-img {
  position: relative;
  height: 170px;
  background: linear-gradient(135deg, #F0EBE4, #E8E2DA);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.mp-listing-photo { width: 100%; height: 100%; object-fit: cover; }
.mp-listing-placeholder { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; }
.mp-listing-badges { position: absolute; top: 8px; left: 8px; display: flex; gap: 4px; }
.mp-save-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 30px;
  height: 30px;
  background: rgba(0,0,0,.5)!important;
  border: none;
  border-radius: 50%;
  color: #fff!important;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all .2s;
}
.mp-save-btn:hover { background: rgba(0,0,0,.7)!important; }
.mp-listing-time-badge {
  position: absolute;
  bottom: 8px;
  left: 8px;
  background: rgba(0,0,0,.6)!important;
  color: #ccc!important;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
}
.mp-listing-views-badge {
  position: absolute;
  bottom: 8px;
  left: 8px;
  background: rgba(0,0,0,.6)!important;
  color: #ccc!important;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
}
.mp-listing-body { padding: 14px; }
.mp-listing-title { font-size: 14px; font-weight: 700; color: #1A1A1A!important; margin-bottom: 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.mp-listing-meta { display: flex; gap: 10px; font-size: 11px; color: #888!important; margin-bottom: 4px; flex-wrap: wrap; }
.mp-listing-location { font-size: 12px; color: #777!important; margin-bottom: 10px; }
.mp-listing-footer { display: flex; justify-content: space-between; align-items: center; padding-top: 10px; border-top: 1px solid #2A2520; }
.mp-listing-price { font-size: 16px; font-weight: 800; color: #BFA164!important; }
.mp-price-unit { font-size: 11px; font-weight: 500; color: var(--fm-s,#6B6356)!important; margin-left: 4px; letter-spacing: .2px; }
.mp-listing-seller { display: flex; align-items: center; gap: 4px; font-size: 11px; color: #888!important; }
.mp-seller-avatar {
  width: 20px;
  height: 20px;
  background: #BFA164!important;
  color: #0A0A0A!important;
  border-radius: 50%;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* BUY REQUEST CARDS */
.mp-buyrequest-card {
  background: #FFFFFF!important;
  border: 1px solid #E8E2DA;
  border-radius: 14px;
  padding: 20px;
  cursor: pointer;
  transition: all .3s;
  height: 100%;
}
.mp-buyrequest-card:hover { border-color: #BFA164; transform: translateY(-3px); }

/* PRICE CARDS */
.mp-price-card {
  background: #FFFFFF!important;
  border: 1px solid #E8E2DA;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  transition: all .3s;
}
.mp-price-card:hover { border-color: #BFA164; }
.mp-price-card.expanded { border-color: #BFA164; box-shadow: 0 4px 16px rgba(191,161,100,.14); }
.mp-price-expand { margin-top: 10px; padding-top: 10px; border-top: 1px dashed rgba(191,161,100,.35); text-align: left; }
.mp-price-loc-row { display: flex; justify-content: space-between; align-items: center; font-size: 12px; color: var(--fm-s,#6B6356); padding: 3px 0; }
.mp-price-loc-row i { color: #BFA164; margin-right: 4px; }

/* RESPONSIVE */
@media (max-width: 991px) {
  .mp-hero-title { font-size: 32px; }
  .mp-search-select { display: none !important; }
}

/* ===== MOBILE — feels-like-an-app responsiveness ===== */
@media (max-width: 768px) {
  /* Hero tighter, search stacks vertically for finger-friendly tap targets */
  .mp-hero { padding: 80px 0 28px; }
  .mp-hero-title { font-size: 24px; line-height: 1.2; }
  .mp-hero-subtitle { font-size: 13px; padding: 0 8px; }
  .mp-search-box .input-group { flex-wrap: wrap; border-radius: 12px; overflow: hidden; }
  .mp-search-box .input-group-text { padding: 10px 12px; }
  .mp-search-box .mp-search-input { flex: 1 1 auto; min-height: 46px; font-size: 15px; }
  .mp-search-box .btn { width: 100%; border-radius: 0; padding: 12px; font-size: 14px; }
  /* Section spacing tighter */
  section { padding-top: 32px !important; padding-bottom: 32px !important; }
  section h2, section h3 { font-size: 18px !important; }
  /* Listing card image taller on phone (more visual weight) */
  .mp-listing-img { height: 160px; }
  .mp-listing-card { border-radius: 12px; }
  /* Allow 2-line title on phones so animal names like "Medium range boran bulls"
     don't get ellipsised mid-word in the 2-column grid. */
  .mp-listing-title {
    font-size: 14px;
    white-space: normal;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-height: 1.25;
    min-height: 2.5em;
  }
  /* Card layout adapts to viewMode toggle (list/grid). List = col-12 big, Grid = col-6 compact. */
  .mp-listing-price { font-size: 16px; white-space: nowrap; }
  .mp-listing-seller { font-size: 12px; max-width: 50%; overflow: hidden; }
  .mp-listing-seller span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  /* Image taller still — it's now full-width so we give it more height. */
  .mp-listing-img { height: 220px; }
  /* In grid view (2-col) the card is half as wide — shrink everything proportionally. */
  .col-6 > .mp-listing-card .mp-listing-img { height: 130px; }
  .col-6 > .mp-listing-card .mp-listing-title { font-size: 13px; min-height: 2.5em; -webkit-line-clamp: 2; }
  .col-6 > .mp-listing-card .mp-listing-footer { flex-direction: column; align-items: flex-start; gap: 6px; }
  .col-6 > .mp-listing-card .mp-listing-price { font-size: 14px; }
  .col-6 > .mp-listing-card .mp-listing-seller { font-size: 10.5px; max-width: 100%; }
  .col-6 > .mp-listing-card .mp-listing-seller span { max-width: 80px; }
  /* Species pills scroll horizontally */
  .mp-species-row { display: flex; gap: 8px; overflow-x: auto; padding-bottom: 6px; -webkit-overflow-scrolling: touch; }
  .mp-species-row::-webkit-scrollbar { display: none; }
  .mp-species-card { flex: 0 0 auto; min-width: 110px; }
  /* Market price cards stack 2-up */
  .mp-price-card { padding: 12px; }
  /* Floating back / theme buttons safe-area aware */
  .mp-back { top: calc(8px + env(safe-area-inset-top, 0px)); left: 10px; width: 36px; height: 36px; }
  .mp-theme-toggle { top: calc(8px + env(safe-area-inset-top, 0px)); right: 10px; width: 36px; height: 36px; }
}
@media (max-width: 575px) {
  .mp-hero { padding: 70px 0 24px; }
  .mp-hero-title { font-size: 22px; }
  .mp-listing-img { height: 140px; }
}
/* List/Grid view toggle — visible on mobile (d-md-none on the wrapper). */
.mp-view-toggle { display: inline-flex; border: 1px solid var(--fm-br,#E0D8D0); border-radius: 8px; overflow: hidden; }
.mp-view-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; padding: 0;
  background: transparent; border: none; color: var(--fm-s,#6B6356);
  font-size: 18px; cursor: pointer; transition: background .15s, color .15s;
}
.mp-view-btn + .mp-view-btn { border-left: 1px solid var(--fm-br,#E0D8D0); }
.mp-view-btn.active { background: #BFA164; color: #FFFFFF; }
.mp-view-btn:not(.active):hover { background: rgba(191,161,100,.1); color: #BFA164; }
.dark-mode .mp-view-toggle { border-color: #2A2520; }
.dark-mode .mp-view-btn { color: #A09888; }
.dark-mode .mp-view-btn + .mp-view-btn { border-left-color: #2A2520; }

/* Theme Toggle */
.mp-theme-toggle {
  position:fixed; top:12px; right:16px; z-index:1050;
  width:40px; height:40px; border-radius:50%;
  background:#FFFFFF; border:1px solid #E0D8D0;
  color:#BFA164; cursor:pointer; transition:all .3s;
  display:flex; align-items:center; justify-content:center;
  box-shadow:0 2px 8px rgba(0,0,0,.1);
}
.mp-theme-toggle:hover { transform:scale(1.1); box-shadow:0 4px 12px rgba(191,161,100,.3); }
.dark-mode .mp-theme-toggle { background:#1E1E1E; border-color:#2A2520; color:#BFA164; box-shadow:0 2px 8px rgba(0,0,0,.4); }
.mp-back {
  position:fixed; top:12px; left:16px; z-index:1050;
  width:40px; height:40px; border-radius:50%;
  background:#FFFFFF; border:1px solid #E0D8D0;
  color:#BFA164; cursor:pointer; transition:all .3s;
  display:flex; align-items:center; justify-content:center;
  box-shadow:0 2px 8px rgba(0,0,0,.1);
}
.mp-back:hover { transform:scale(1.1); box-shadow:0 4px 12px rgba(191,161,100,.3); }
.dark-mode .mp-back { background:#1E1E1E; border-color:#2A2520; color:#BFA164; box-shadow:0 2px 8px rgba(0,0,0,.4); }

/* ===== DARK MODE OVERRIDES ===== */
.dark-mode { color:#F5F0E8; }
.dark-mode section[style*="background:#F7F5F2"] { background:#0F0F0F!important; }
.dark-mode section[style*="background:#FFFFFF"] { background:#0A0A0A!important; }

/* Cards */
.dark-mode .mp-listing-card,
.dark-mode .mp-species-card,
.dark-mode .mp-buyrequest-card,
.dark-mode .mp-price-card { background:#141414!important; border-color:#2A2520!important; }
.dark-mode .mp-listing-card:hover,
.dark-mode .mp-species-card:hover,
.dark-mode .mp-buyrequest-card:hover,
.dark-mode .mp-price-card:hover { border-color:#BFA164!important; box-shadow:0 4px 12px rgba(191,161,100,.15)!important; }

/* Text */
.dark-mode .mp-listing-title { color:#F5F0E8!important; }
.dark-mode .mp-species-name { color:#F5F0E8!important; }
.dark-mode .mp-listing-meta { color:#A09888!important; }
.dark-mode .mp-listing-seller { color:#A09888!important; }
.dark-mode .mp-listing-location { color:#A09888!important; }
.dark-mode h5[style*="color:#BFA164"] { color:#BFA164!important; }

/* Buy requests inside dark cards */
.dark-mode .mp-buyrequest-card h6 { color:#F5F0E8!important; }
.dark-mode .mp-buyrequest-card p { color:#A09888!important; }
.dark-mode .mp-buyrequest-card span { color:#6B6356!important; }

/* Market price */
.dark-mode .mp-price-card div[style*="color:#1A1A1A"] { color:#F5F0E8!important; }

/* Search box */
.dark-mode .mp-search-box .input-group { background:#141414!important; border-color:#2A2520!important; }
.dark-mode .mp-search-input { background:transparent!important; color:#F5F0E8!important; }
.dark-mode .mp-search-select { background:#1A1A1A!important; border-color:#2A2520!important; color:#A09888!important; }

/* Listing image placeholder */
.dark-mode .mp-listing-img { background:linear-gradient(135deg,#1A1510,#0F0F0F)!important; }

/* Seller dot */
.dark-mode .mp-seller-dot { background:#1E1E1E!important; }

/* Borders */
.dark-mode .mp-buyrequest-card > div { border-color:#1E1E1E!important; }

/* Species icon colors in dark */
.dark-mode .mp-species-count { color:#6B6356!important; }

</style>
